// Copyright 2021-2026 The Connect Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
var __await = (this && this.__await) || function (v) { return this instanceof __await ? (this.v = v, this) : new __await(v); }
var __asyncGenerator = (this && this.__asyncGenerator) || function (thisArg, _arguments, generator) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var g = generator.apply(thisArg, _arguments || []), i, q = [];
    return i = Object.create((typeof AsyncIterator === "function" ? AsyncIterator : Object).prototype), verb("next"), verb("throw"), verb("return", awaitReturn), i[Symbol.asyncIterator] = function () { return this; }, i;
    function awaitReturn(f) { return function (v) { return Promise.resolve(v).then(f, reject); }; }
    function verb(n, f) { if (g[n]) { i[n] = function (v) { return new Promise(function (a, b) { q.push([n, v, a, b]) > 1 || resume(n, v); }); }; if (f) i[n] = f(i[n]); } }
    function resume(n, v) { try { step(g[n](v)); } catch (e) { settle(q[0][3], e); } }
    function step(r) { r.value instanceof __await ? Promise.resolve(r.value.v).then(fulfill, reject) : settle(q[0][2], r); }
    function fulfill(value) { resume("next", value); }
    function reject(value) { resume("throw", value); }
    function settle(f, v) { if (f(v), q.shift(), q.length) resume(q[0][0], q[0][1]); }
};
var __asyncValues = (this && this.__asyncValues) || function (o) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var m = o[Symbol.asyncIterator], i;
    return m ? m.call(o) : (o = typeof __values === "function" ? __values(o) : o[Symbol.iterator](), i = {}, verb("next"), verb("throw"), verb("return"), i[Symbol.asyncIterator] = function () { return this; }, i);
    function verb(n) { i[n] = o[n] && function (v) { return new Promise(function (resolve, reject) { v = o[n](v), settle(resolve, reject, v.done, v.value); }); }; }
    function settle(resolve, reject, d, v) { Promise.resolve(v).then(function(v) { resolve({ value: v, done: d }); }, reject); }
};
import { Code, ConnectError, createContextValues } from "@connectrpc/connect";
import { compressedFlag, createClientMethodSerializers, createMethodUrl, runStreamingCall, runUnaryCall, } from "@connectrpc/connect/protocol";
import { endStreamFlag, endStreamFromJson, requestHeader, } from "@connectrpc/connect/protocol-connect";
import { runWebTransportCall } from "./webtransport-helper.js";
const flagEnvelopeData = 0x00;
const flagEnvelopeHeaders = 0x06;
/**
 * Create a Transport for the Connect protocol running over WebTransport.
 */
export function createConnectWebTransportTransport(options) {
    var _a;
    const useBinaryFormat = (_a = options.useBinaryFormat) !== null && _a !== void 0 ? _a : false;
    return {
        async unary(method, signal, timeoutMs, header, message, contextValues) {
            const { serialize, parse } = createClientMethodSerializers(method, useBinaryFormat, options.jsonOptions, options.binaryOptions);
            timeoutMs =
                timeoutMs === undefined
                    ? options.defaultTimeoutMs
                    : timeoutMs <= 0
                        ? undefined
                        : timeoutMs;
            return await runUnaryCall({
                interceptors: options.interceptors,
                signal,
                timeoutMs,
                req: {
                    stream: false,
                    service: method.parent,
                    method,
                    requestMethod: "POST",
                    url: createMethodUrl(options.baseUrl, method),
                    header: requestHeader(method.methodKind, useBinaryFormat, timeoutMs, header, false),
                    contextValues: contextValues !== null && contextValues !== void 0 ? contextValues : createContextValues(),
                    message,
                },
                next: async (req) => {
                    var _a, e_1, _b, _c;
                    const resolvedSession = typeof options.session === "function"
                        ? await options.session()
                        : await options.session;
                    const path = `/${method.parent.typeName}/${method.name}`;
                    await resolvedSession.ready;
                    const requestMessageBytes = serialize(req.message);
                    function singleMessage() {
                        return __asyncGenerator(this, arguments, function* singleMessage_1() {
                            yield yield __await(requestMessageBytes);
                        });
                    }
                    const { responseHeaders, responseMessages } = await runWebTransportCall(resolvedSession, req.header, singleMessage(), flagEnvelopeHeaders, flagEnvelopeData, path);
                    let responseMessage;
                    let endStreamReceived = false;
                    const trailer = new Headers();
                    try {
                        for (var _d = true, responseMessages_1 = __asyncValues(responseMessages), responseMessages_1_1; responseMessages_1_1 = await responseMessages_1.next(), _a = responseMessages_1_1.done, !_a; _d = true) {
                            _c = responseMessages_1_1.value;
                            _d = false;
                            const env = _c;
                            if (env.flags === flagEnvelopeData ||
                                env.flags === compressedFlag) {
                                if (responseMessage !== undefined) {
                                    throw new ConnectError("unary stream has multiple messages", Code.Unimplemented);
                                }
                                responseMessage = env.data;
                            }
                            else if (env.flags === endStreamFlag) {
                                endStreamReceived = true;
                                const endStream = endStreamFromJson(env.data);
                                if (endStream.error) {
                                    const error = endStream.error;
                                    responseHeaders.forEach((value, key) => {
                                        error.metadata.append(key, value);
                                    });
                                    throw error;
                                }
                                endStream.metadata.forEach((value, key) => {
                                    trailer.set(key, value);
                                });
                            }
                            else {
                                throw new ConnectError(`protocol error: unexpected envelope flag 0x${env.flags.toString(16)}`, Code.Internal);
                            }
                        }
                    }
                    catch (e_1_1) { e_1 = { error: e_1_1 }; }
                    finally {
                        try {
                            if (!_d && !_a && (_b = responseMessages_1.return)) await _b.call(responseMessages_1);
                        }
                        finally { if (e_1) throw e_1.error; }
                    }
                    if (!endStreamReceived) {
                        throw new ConnectError("missing EndStreamResponse", Code.Internal);
                    }
                    if (responseMessage === undefined) {
                        throw new ConnectError("unary stream has no message", Code.Unimplemented);
                    }
                    return {
                        stream: false,
                        service: method.parent,
                        method,
                        header: responseHeaders,
                        message: parse(responseMessage),
                        trailer,
                    };
                },
            });
        },
        async stream(method, signal, timeoutMs, header, input, contextValues) {
            const { serialize, parse } = createClientMethodSerializers(method, useBinaryFormat, options.jsonOptions, options.binaryOptions);
            timeoutMs =
                timeoutMs === undefined
                    ? options.defaultTimeoutMs
                    : timeoutMs <= 0
                        ? undefined
                        : timeoutMs;
            return await runStreamingCall({
                interceptors: options.interceptors,
                timeoutMs,
                signal,
                req: {
                    stream: true,
                    service: method.parent,
                    method,
                    requestMethod: "POST",
                    url: createMethodUrl(options.baseUrl, method),
                    header: requestHeader(method.methodKind, useBinaryFormat, timeoutMs, header, false),
                    contextValues: contextValues !== null && contextValues !== void 0 ? contextValues : createContextValues(),
                    message: input,
                },
                next: async (req) => {
                    const resolvedSession = typeof options.session === "function"
                        ? await options.session()
                        : await options.session;
                    const path = `/${method.parent.typeName}/${method.name}`;
                    await resolvedSession.ready;
                    function serializeRequestMessages() {
                        return __asyncGenerator(this, arguments, function* serializeRequestMessages_1() {
                            var _a, e_2, _b, _c;
                            try {
                                for (var _d = true, _e = __asyncValues(req.message), _f; _f = yield __await(_e.next()), _a = _f.done, !_a; _d = true) {
                                    _c = _f.value;
                                    _d = false;
                                    const msg = _c;
                                    yield yield __await(serialize(msg));
                                }
                            }
                            catch (e_2_1) { e_2 = { error: e_2_1 }; }
                            finally {
                                try {
                                    if (!_d && !_a && (_b = _e.return)) yield __await(_b.call(_e));
                                }
                                finally { if (e_2) throw e_2.error; }
                            }
                        });
                    }
                    const { responseHeaders, responseMessages } = await runWebTransportCall(resolvedSession, req.header, serializeRequestMessages(), flagEnvelopeHeaders, flagEnvelopeData, path);
                    const trailer = new Headers();
                    function parseResponseBody() {
                        return __asyncGenerator(this, arguments, function* parseResponseBody_1() {
                            var _a, e_3, _b, _c;
                            let endStreamReceived = false;
                            try {
                                for (var _d = true, responseMessages_2 = __asyncValues(responseMessages), responseMessages_2_1; responseMessages_2_1 = yield __await(responseMessages_2.next()), _a = responseMessages_2_1.done, !_a; _d = true) {
                                    _c = responseMessages_2_1.value;
                                    _d = false;
                                    const env = _c;
                                    if (env.flags === flagEnvelopeData ||
                                        env.flags === compressedFlag) {
                                        yield yield __await(parse(env.data));
                                    }
                                    else if (env.flags === endStreamFlag) {
                                        endStreamReceived = true;
                                        const endStream = endStreamFromJson(env.data);
                                        if (endStream.error) {
                                            const error = endStream.error;
                                            responseHeaders.forEach((value, key) => {
                                                error.metadata.append(key, value);
                                            });
                                            throw error;
                                        }
                                        endStream.metadata.forEach((value, key) => {
                                            trailer.set(key, value);
                                        });
                                    }
                                    else {
                                        throw new ConnectError(`protocol error: unexpected envelope flag 0x${env.flags.toString(16)}`, Code.Internal);
                                    }
                                }
                            }
                            catch (e_3_1) { e_3 = { error: e_3_1 }; }
                            finally {
                                try {
                                    if (!_d && !_a && (_b = responseMessages_2.return)) yield __await(_b.call(responseMessages_2));
                                }
                                finally { if (e_3) throw e_3.error; }
                            }
                            if (!endStreamReceived) {
                                throw new ConnectError("missing EndStreamResponse", Code.Internal);
                            }
                        });
                    }
                    return {
                        stream: true,
                        service: method.parent,
                        method,
                        header: responseHeaders,
                        trailer,
                        message: parseResponseBody(),
                    };
                },
            });
        },
    };
}
